import { useStore } from "../state/store";
import { validateNamePart } from "../model/validation";
import { resolveRef } from "../model/identifiers";
import { VANILLA_ITEMS, findVanillaItem } from "../model/vanillaItems";
import { VANILLA_TAGS, findVanillaTag } from "../model/vanillaTags";
import { EmptyState, FullIdPreview } from "../ui/common";
import type { ItemRef, Project, Recipe } from "../model/types";

/**
 * Id of the single shared <datalist> of vanilla items. One list is rendered per
 * panel and referenced by every picker, so adding an ingredient row costs one
 * <input> rather than another few hundred <option> elements.
 */
const VANILLA_LIST_ID = "vanilla-item-list";

/** Same idea as `VANILLA_LIST_ID`, for the vanilla item-tag catalogue. */
const VANILLA_TAG_LIST_ID = "vanilla-tag-list";

export function RecipesPanel() {
  const project = useStore((s) => s.project);
  const selectedUuid = useStore((s) => s.ui.selectedRecipeUuid);
  const selectRecipe = useStore((s) => s.selectRecipe);
  const addRecipe = useStore((s) => s.addRecipe);
  const removeRecipe = useStore((s) => s.removeRecipe);

  const selected = project.recipes.find((r) => r.uuid === selectedUuid) ?? project.recipes[0];

  return (
    <div className="panel split">
      <PickerDatalists />
      <div className="list-column">
        <div className="list-header">
          <h2>Recipes</h2>
          <button className="btn primary" onClick={() => addRecipe()}>
            + Recipe
          </button>
        </div>
        <ul className="entity-list">
          {project.recipes.map((recipe) => (
            <li
              key={recipe.uuid}
              className={recipe.uuid === selected?.uuid ? "active" : ""}
              onClick={() => selectRecipe(recipe.uuid)}
            >
              <span className="entity-name">{recipe.id}</span>
              <span className="muted">{recipe.shape}</span>
            </li>
          ))}
          {project.recipes.length === 0 && <li className="muted">No recipes yet.</li>}
        </ul>
      </div>

      <div className="editor-column">
        {selected ? (
          <RecipeEditor key={selected.uuid} recipe={selected} project={project} onDelete={() => removeRecipe(selected.uuid)} />
        ) : (
          <EmptyState>Add a recipe to get started.</EmptyState>
        )}
      </div>
    </div>
  );
}

function RecipeEditor({ recipe, project, onDelete }: { recipe: Recipe; project: Project; onDelete: () => void }) {
  const updateRecipe = useStore((s) => s.updateRecipe);
  const idValid = validateNamePart(recipe.id);

  const patch = (p: Partial<Recipe>) => updateRecipe(recipe.uuid, p);

  return (
    <div className="editor">
      <label className="field">
        <span className="field-label">Recipe id (name-part)</span>
        <input
          className={idValid ? "" : "invalid"}
          value={recipe.id}
          spellCheck={false}
          onChange={(e) => patch({ id: e.target.value.trim() })}
        />
      </label>
      <div className="preview-line">
        Identifier: <FullIdPreview namespace={project.namespace} id={recipe.id} />
      </div>

      <div className="field-row">
        <label className="field">
          <span className="field-label">Type</span>
          <select value={recipe.shape} onChange={(e) => patch({ shape: e.target.value as Recipe["shape"] })}>
            <option value="shapeless">Shapeless</option>
            <option value="shaped">Shaped</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">Crafting station tag</span>
          <input value={recipe.tags.join(", ")} onChange={(e) => patch({ tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} />
        </label>
      </div>

      {recipe.shape === "shapeless" ? (
        <ShapelessEditor recipe={recipe} project={project} patch={patch} />
      ) : (
        <ShapedEditor recipe={recipe} project={project} patch={patch} />
      )}

      <div className="field-row align-top">
        <div className="field grow">
          <span className="field-label">Result</span>
          <ItemRefEditor
            value={recipe.result}
            project={project}
            allowTags={false}
            onChange={(result) => patch({ result })}
          />
        </div>
        <label className="field narrow">
          <span className="field-label">Count</span>
          <input
            type="number"
            min={1}
            max={64}
            value={recipe.resultCount}
            onChange={(e) => patch({ resultCount: Math.max(1, Number(e.target.value) || 1) })}
          />
        </label>
      </div>
      <div className="preview-line">Produces: <code>{resolveRef(project.namespace, recipe.result)}</code> ×{recipe.resultCount}</div>

      <div className="editor-footer">
        <button className="btn danger" onClick={onDelete}>
          Delete recipe
        </button>
      </div>
    </div>
  );
}

function ShapelessEditor({ recipe, project, patch }: { recipe: Recipe; project: Project; patch: (p: Partial<Recipe>) => void }) {
  const setIngredient = (i: number, ref: ItemRef) => {
    const ingredients = [...recipe.ingredients];
    ingredients[i] = ref;
    patch({ ingredients });
  };
  return (
    <div className="field">
      <span className="field-label">Ingredients</span>
      {recipe.ingredients.map((ref, i) => (
        <div className="ingredient-row" key={i}>
          <ItemRefEditor value={ref} project={project} onChange={(r) => setIngredient(i, r)} />
          <button
            className="btn"
            onClick={() => patch({ ingredients: recipe.ingredients.filter((_, j) => j !== i) })}
          >
            ✕
          </button>
        </div>
      ))}
      <button className="btn" onClick={() => patch({ ingredients: [...recipe.ingredients, { kind: "vanilla", id: "minecraft:stick" }] })}>
        + Ingredient
      </button>
    </div>
  );
}

function ShapedEditor({ recipe, project, patch }: { recipe: Recipe; project: Project; patch: (p: Partial<Recipe>) => void }) {
  const keys = Object.keys(recipe.key);
  const setPatternRow = (r: number, value: string) => {
    const pattern = [...recipe.pattern];
    pattern[r] = value.slice(0, 3);
    patch({ pattern });
  };
  const setKeyRef = (k: string, ref: ItemRef) => patch({ key: { ...recipe.key, [k]: ref } });
  const addKey = () => {
    const used = new Set(keys);
    const letter = "ABCDEFGHIJ".split("").find((c) => !used.has(c)) ?? "X";
    patch({ key: { ...recipe.key, [letter]: { kind: "vanilla", id: "minecraft:stick" } } });
  };
  const removeKey = (k: string) => {
    const next = { ...recipe.key };
    delete next[k];
    patch({ key: next });
  };

  return (
    <>
      <div className="field">
        <span className="field-label">Pattern (up to 3 rows × 3 chars; space = empty)</span>
        {[0, 1, 2].map((r) => (
          <input
            key={r}
            className="pattern-row"
            maxLength={3}
            spellCheck={false}
            value={recipe.pattern[r] ?? ""}
            placeholder="   "
            onChange={(e) => setPatternRow(r, e.target.value)}
          />
        ))}
      </div>
      <div className="field">
        <span className="field-label">Key (pattern character → item)</span>
        {keys.map((k) => (
          <div className="ingredient-row" key={k}>
            <code className="key-char">{k}</code>
            <ItemRefEditor value={recipe.key[k]} project={project} onChange={(r) => setKeyRef(k, r)} />
            <button className="btn" onClick={() => removeKey(k)}>
              ✕
            </button>
          </div>
        ))}
        <button className="btn" onClick={addKey}>
          + Key
        </button>
      </div>
    </>
  );
}

/**
 * The vanilla item and item-tag catalogues, rendered once per panel. Every
 * vanilla picker points its `list` attribute at one of these, which turns the
 * plain text field into a type-to-filter combo box while still accepting any
 * hand-typed id.
 */
function PickerDatalists() {
  return (
    <>
      <datalist id={VANILLA_LIST_ID}>
        {VANILLA_ITEMS.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name} · {item.group}
          </option>
        ))}
      </datalist>
      <datalist id={VANILLA_TAG_LIST_ID}>
        {VANILLA_TAGS.map((tag) => (
          <option key={tag.id} value={tag.id}>
            {tag.name} · {tag.group}
          </option>
        ))}
      </datalist>
    </>
  );
}

/**
 * The four things a recipe slot can be. Tags ("any planks") are a separate
 * source from items because they resolve to a GROUP of items, and the picker
 * for them is a different catalogue, but they share the project/vanilla axis.
 */
type RefSource = "vanilla" | "vanilla_tag" | "project" | "project_tag";

function sourceOf(ref: ItemRef): RefSource {
  if (ref.kind === "project") return ref.tag ? "project_tag" : "project";
  return ref.tag ? "vanilla_tag" : "vanilla";
}

/** A sensible starting id when the user switches a slot to another source. */
function refForSource(source: RefSource, project: Project, current: ItemRef): ItemRef {
  switch (source) {
    case "vanilla":
      return { kind: "vanilla", id: findVanillaItem(current.id) ? current.id : "minecraft:stick" };
    case "vanilla_tag":
      return { kind: "vanilla", id: findVanillaTag(current.id) ? current.id : "minecraft:planks", tag: true };
    case "project":
      return { kind: "project", id: project.items[0]?.id ?? project.guideItemId };
    case "project_tag":
      return { kind: "project", id: current.kind === "project" ? current.id : "", tag: true };
  }
}

/**
 * Picker for an ItemRef: a vanilla or project item, or a vanilla or project
 * item TAG. `allowTags` is off for a recipe result, which must be one item.
 */
function ItemRefEditor({
  value,
  project,
  onChange,
  allowTags = true,
}: {
  value: ItemRef;
  project: Project;
  onChange: (ref: ItemRef) => void;
  allowTags?: boolean;
}) {
  const source = sourceOf(value);
  return (
    <div className="itemref">
      <select
        className="itemref-kind"
        aria-label="Item source"
        value={source}
        onChange={(e) => onChange(refForSource(e.target.value as RefSource, project, value))}
      >
        <option value="vanilla">Vanilla</option>
        <option value="project">Project item</option>
        {/* A stale tag selection stays listed even when tags are disallowed, so
            the control shows the truth rather than silently re-pointing it. */}
        {(allowTags || source === "vanilla_tag") && <option value="vanilla_tag">Vanilla tag</option>}
        {(allowTags || source === "project_tag") && <option value="project_tag">Project tag</option>}
      </select>
      {source === "vanilla" && (
        <VanillaRefPicker value={value.id} onChange={(id) => onChange({ kind: "vanilla", id })} />
      )}
      {source === "vanilla_tag" && (
        <VanillaTagPicker value={value.id} onChange={(id) => onChange({ kind: "vanilla", id, tag: true })} />
      )}
      {source === "project" && (
        <ProjectRefPicker value={value.id} project={project} onChange={(id) => onChange({ kind: "project", id })} />
      )}
      {source === "project_tag" && (
        <ProjectTagPicker
          value={value.id}
          project={project}
          onChange={(id) => onChange({ kind: "project", id, tag: true })}
        />
      )}
    </div>
  );
}

function VanillaRefPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const known = findVanillaItem(value);
  const malformed = value.length > 0 && !value.includes(":");

  return (
    <div className="itemref-value">
      <input
        className={`itemref-id${malformed ? " invalid" : ""}`}
        list={VANILLA_LIST_ID}
        value={value}
        spellCheck={false}
        aria-label="Vanilla item id"
        placeholder="Search vanilla items, e.g. minecraft:stick"
        onChange={(e) => onChange(e.target.value.trim())}
      />
      {known ? (
        <span className="itemref-note">
          {known.name} · {known.group}
        </span>
      ) : malformed ? (
        <span className="itemref-note warn">Needs a full id, e.g. minecraft:stick</span>
      ) : (
        <span className="itemref-note dim">Not in the catalogue. Exported as typed.</span>
      )}
    </div>
  );
}

function VanillaTagPicker({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  const known = findVanillaTag(value);
  const malformed = value.length > 0 && !value.includes(":");

  return (
    <div className="itemref-value">
      <input
        className={`itemref-id${malformed ? " invalid" : ""}`}
        list={VANILLA_TAG_LIST_ID}
        value={value}
        spellCheck={false}
        aria-label="Vanilla item tag"
        placeholder="Search vanilla tags, e.g. minecraft:planks"
        onChange={(e) => onChange(e.target.value.trim())}
      />
      {known ? (
        <span className="itemref-note">
          {known.name} · {known.group}
        </span>
      ) : malformed ? (
        <span className="itemref-note warn">Needs a full id, e.g. minecraft:planks</span>
      ) : (
        <span className="itemref-note dim">Not in the catalogue. Exported as typed.</span>
      )}
    </div>
  );
}

/**
 * A tag defined by the pack itself. Unlike project items, tags are not objects
 * in this document (they live on item/block definitions the tool doesn't
 * model), so this is free text with the composed id shown back.
 */
function ProjectTagPicker({ value, project, onChange }: { value: string; project: Project; onChange: (id: string) => void }) {
  const valid = validateNamePart(value);

  return (
    <div className="itemref-value">
      <input
        className={`itemref-id${valid ? "" : " invalid"}`}
        value={value}
        spellCheck={false}
        aria-label="Project item tag"
        placeholder="Tag name-part, e.g. paintbrush"
        onChange={(e) => onChange(e.target.value.trim())}
      />
      {valid ? (
        <span className="itemref-note dim">
          Any item tagged {project.namespace || "?"}:{value}
        </span>
      ) : (
        <span className="itemref-note warn">Tag name-part must match ^[a-z0-9_]+$.</span>
      )}
    </div>
  );
}

function ProjectRefPicker({ value, project, onChange }: { value: string; project: Project; onChange: (id: string) => void }) {
  // A ref can point at an item that was since renamed or deleted. Keep it as an
  // option so the select shows the truth instead of silently displaying (and,
  // on the next edit, saving) some unrelated item.
  const missing = value !== project.guideItemId && !project.items.some((i) => i.id === value);

  return (
    <div className="itemref-value">
      <select
        className={`itemref-id${missing ? " invalid" : ""}`}
        value={value}
        aria-label="Project item"
        onChange={(e) => onChange(e.target.value)}
      >
        <option value={project.guideItemId}>{project.guideItemId} (guide)</option>
        {project.items.map((i) => (
          <option key={i.uuid} value={i.id}>
            {i.id}
          </option>
        ))}
        {missing && <option value={value}>{value || "(none)"} (missing)</option>}
      </select>
      {missing ? (
        <span className="itemref-note warn">No project item with this id. Pick another.</span>
      ) : (
        <span className="itemref-note dim">
          {project.namespace || "?"}:{value}
        </span>
      )}
    </div>
  );
}
